import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "@fontsource/chivo";
import { FiPrinter, FiCheckCircle } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

const NewOrderModal = ({ orderIds, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-md p-6 max-w-md shadow-md text-center font-[chivo]"
      >
<motion.h2
  className="text-2xl font-bold mb-4 text-[#009FB7]"
  animate={{
    scale: [1, 1.05, 1],
    opacity: [1, 0.8, 1],
  }}
  transition={{
    duration: 1.5,
    repeat: Infinity,
    ease: "easeInOut",
  }}
>
  🆕 Nuovi Ordini Ricevuti
</motion.h2>

        <p className="mb-2">Hai ricevuto i seguenti nuovi ordini:</p>
        <motion.ul
          className="mb-4 font-semibold"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {orderIds.map((id) => (
            <motion.li
              key={id}
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Ordine #{id}
            </motion.li>
          ))}
        </motion.ul>
        <motion.button
          onClick={onClose}
          className="bg-[#009FB7] text-white px-4 py-2 rounded hover:bg-[#007d99]"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Conferma
        </motion.button>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

const ModalitaServizio = ({ newOrderIds, setNewOrderIds }) => {
  const [orders, setOrders] = useState([]);
  const didInitRef = useRef(false);


// remember last known stato per order id
const prevStatusRef = useRef({}); // { [id]: "pagato" | "non pagato" }
const mountedAtRef = useRef(new Date()); // skip anything older than page mount

const fetchOrders = () => {
  axios.get("https://api.lidodavittorio.it/api/orders")
    .then((res) => {
      const all = res.data.sort((a,b) => new Date(a.created_at) - new Date(b.created_at));

      console.log("📥 Fetched orders:", all.map(o => ({
        id: o.id,
        stato: o.stato,
        completato: o.completato,
        created_at: o.created_at
      })));

      // build current status map
      const currStatus = {};
      for (const o of all) currStatus[o.id] = o.stato;

      if (didInitRef.current) {
        const newlyPaidIds = all
          .filter(o => o.completato === "NO" && o.stato === "pagato")
          .filter(o => Object.prototype.hasOwnProperty.call(prevStatusRef.current, o.id))
          .filter(o => prevStatusRef.current[o.id] !== "pagato")
          .filter(o => new Date(o.created_at) >= mountedAtRef.current)
          .map(o => o.id);

        console.log("🔍 prevStatusRef:", prevStatusRef.current);
        console.log("🔍 currStatus:", currStatus);
        console.log("✨ Newly paid IDs:", newlyPaidIds);

        if (newlyPaidIds.length > 0) {
          setNewOrderIds(prev => Array.from(new Set([...prev, ...newlyPaidIds])));
        }
      } else {
        console.log("⏭ First fetch, skip notifications");
        didInitRef.current = true;
      }

      // update snapshot AFTER computing transitions
      prevStatusRef.current = currStatus;

      // only paid & not completed
      const toBeCompleted = all.filter(o => o.completato === "NO" && o.stato === "pagato");
      console.log("📋 Orders to be completed:", toBeCompleted.map(o => o.id));
      setOrders(toBeCompleted);
    })
    .catch(err => console.error("❌ Errore nel caricamento ordini:", err));
};

useEffect(() => {
  fetchOrders();
  const id = setInterval(fetchOrders, 5000);
  return () => clearInterval(id);
}, []);

 const handleComplete = (id) => {
  const confirmed = window.confirm(
    "Sei sicuro di voler completare questo ordine?"
  );
  if (!confirmed) return;

  axios
    .post(`https://api.lidodavittorio.it/api/orders/${id}/mark-completed`)
    .then(() => {
      setOrders((prev) => prev.filter((order) => order.id !== id));
      setNewOrderIds((prev) => prev.filter((oid) => oid !== id));
    })
    .catch((err) => console.error("Errore completamento ordine:", err));
};


  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000); // update every second

    return () => clearInterval(interval);
  }, []);

  const getTimeElapsed = (createdAt) => {
    const diffMs = now - new Date(createdAt).getTime();
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const [checkedItems, setCheckedItems] = useState({});
  const toggleItem = (orderId, itemKey) => {
    setCheckedItems((prev) => {
      const currentOrder = prev[orderId] || [];
      const alreadyChecked = currentOrder.includes(itemKey);
      const updated = alreadyChecked
        ? currentOrder.filter((i) => i !== itemKey)
        : [...currentOrder, itemKey];

      return { ...prev, [orderId]: updated };
    });
  };

  return (
    <div className="text-black font-[chivo] w-full">
      <h1 className="text-3xl font-bold text-[#009FB7] mb-8 text-center no-print">
        Ordini da Preparare
      </h1>

      {newOrderIds.length > 0 && (
         <NewOrderModal
           orderIds={newOrderIds}
           onClose={() => setNewOrderIds([])}
         />
      )}

      {orders.length === 0 ? (
        <p className="text-center text-gray-600 no-print">
          Nessun ordine da completare.
        </p>
      ) : (
        <div className="flex flex-wrap justify-center gap-6 px-2 no-print">
          {orders.map((order) => (
            <div
              key={order.id}
              id={`order-${order.id}`}
              className="bg-white shadow-md border border-gray-300 p-4 relative overflow-hidden rounded-sm w-full sm:w-[48%] md:w-[31%] xl:w-[24%] flex flex-col min-h-[460px]"
            >
              <div className="text-center font-bold text-xl border-b pb-2 mb-4">
                Ordine #{order.id}
              </div>

              <p>
                <strong>Posto:</strong> {order.posto}
              </p>
              <div className="flex justify-between items-center text-sm">
                <p>
                  <strong>Data:</strong>{" "}
                  {new Date(order.created_at).toLocaleString()}
                </p>
                <p className="text-red-500 font-bold">
                  ⏱ {getTimeElapsed(order.created_at)}
                </p>
              </div>

              <hr className="my-2" />

              {/* Grouped items by categoria */}
              {(() => {
                const rawItems = JSON.parse(order.ordine);

                // Define category groups
                const cucinaCategories = ["PANINI", "TRAMEZZINI","INSALATE","FRUTTA","BEACH BRUNCH"];
                const barCategories = [
                  "CAFFETTERIA",
                  "ANALCOLICI",
                  "COCKTAIL",
                  "BIRRE",
                  "BIRRE ALLA SPINA",
                  "VINO",
                  "DOLCI",
                  "PATATINE",
                ];
                const priorityCategories = [
                  ...cucinaCategories,
                  ...barCategories,
                ];

                const grouped = {};

                Object.entries(rawItems).forEach(([key, item]) => {
                  const category = key.split("-")[0];
                  if (!grouped[category]) grouped[category] = [];
                  grouped[category].push(item);
                });

                // Sort with cucina/bar categories first
                const sortedCategories = [
                  ...priorityCategories.filter((cat) => grouped[cat]),
                  ...Object.keys(grouped).filter(
                    (cat) => !priorityCategories.includes(cat)
                  ),
                ];

                return (
                  <div className="space-y-3 text-sm">
                    {sortedCategories.map((category) => {
                      const items = grouped[category];

                      return (
                        <div key={category}>
                          <h4 className="font-semibold text-[#009FB7] border-b border-dashed border-gray-300 mb-1">
                            {category}
                          </h4>
                          <ul className="space-y-1">
                            {items.map((item, index) => {
                              const itemKey = `${category}-${index}`;
                              const isChecked =
                                checkedItems[order.id]?.includes(itemKey);

                              const isSpecial =
                                cucinaCategories.includes(category) ||
                                barCategories.includes(category);
                              const isCucina =
                                cucinaCategories.includes(category);
                              const isBar = barCategories.includes(category);

                              return (
                                <li
                                  key={index}
                                  onClick={() => toggleItem(order.id, itemKey)}
                                  className={`flex justify-between cursor-pointer transition ${
                                    isSpecial ? "text-base font-bold" : ""
                                  } ${
                                    isCucina
                                      ? "text-[#0a0a0a]"
                                      : isBar
                                      ? "text-[#0a0a0a]"
                                      : ""
                                  } ${
                                    isChecked
                                      ? "line-through text-gray-400 opacity-60"
                                      : ""
                                  }`}
                                >
                                  <span>
                                    {item.nome}
                                    {isCucina && (
                                      <span className="ml-2 text-xs font-bold text-red-500">
                                        × CUCINA
                                      </span>
                                    )}
                                    {isBar && (
                                      <span className="ml-2 text-xs font-bold text-blue-500">
                                        × BAR
                                      </span>
                                    )}
                                  </span>
                                  <span>
                                    {item.qty} x €{item.prezzo.toFixed(2)}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <hr className="my-2" />

              <p className="text-right text-lg font-bold text-[#009FB7]">
                Totale: €{order.prezzo.toFixed(2)}
              </p>

              <div className="flex gap-2 mt-auto pt-4 no-print">
                <button
                  onClick={() => handleComplete(order.id)}
                  className="flex-1 bg-[#009FB7] text-white rounded py-2 text-sm hover:bg-[#007d99] transition flex items-center justify-center gap-1"
                >
                  <FiCheckCircle className="text-white" />
                  Completato
                </button>
                <button
                  onClick={() => {
                    const printContent = document
                      .getElementById(`order-${order.id}`)
                      .cloneNode(true);
                    const buttons = printContent.querySelector(".no-print");
                    if (buttons) buttons.remove();

                    const printWindow = window.open("", "_blank");
                    printWindow.document.write(`<!DOCTYPE html>
                        <html>
                          <head>
                            <title>Ordine #${order.id}</title>
                            <style>
                              @page { size: auto; margin: 10mm; }
                              body { font-family: Arial; margin: 0; padding: 20px; }
                            </style>
                          </head>
                          <body>
                            ${printContent.outerHTML}
                            <script>
                              window.onload = function() {
                                setTimeout(function() {
                                  window.print();
                                  window.close();
                                }, 100);
                              };
                            </script>
                          </body>
                        </html>`);
                    printWindow.document.close();
                  }}
                  className="flex-1 bg-gray-300 text-black rounded py-2 text-sm hover:bg-gray-400 transition flex items-center justify-center gap-1"
                >
                  <FiPrinter />
                  Stampa
                </button>
              </div>

              <div className="absolute bottom-0 left-0 w-full h-4 bg-white [mask-image:linear-gradient(135deg,black_25%,transparent_25%),linear-gradient(225deg,black_25%,transparent_25%)] mask-size-16 mask-repeat-x" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModalitaServizio;
